import { useEffect, useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { apiClient } from '@/api/client';
import { refreshReactionEmojis } from '@/components/chat/MessageReactions';

interface ReactionEmoji {
  id: string;
  emoji: string;
  name: string;
  order: number;
  enabled: boolean;
}

const DEFAULT_EMOJIS: Array<{ emoji: string; name: string }> = [
  { emoji: '👍', name: 'нравится' },
  { emoji: '❤️', name: 'любовь' },
  { emoji: '😂', name: 'смех' },
  { emoji: '🎉', name: 'праздник' },
  { emoji: '😮', name: 'удивление' },
  { emoji: '😢', name: 'грусть' },
  { emoji: '🔥', name: 'огонь' },
  { emoji: '👏', name: 'аплодисменты' },
];

export default function AdminReactions() {
  const [items, setItems] = useState<ReactionEmoji[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmoji, setNewEmoji] = useState('');
  const [newName, setNewName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<ReactionEmoji[]>('/api/admin/reactions');
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!newEmoji.trim() || !newName.trim()) return;
    await apiClient.post('/api/admin/reactions', {
      emoji: newEmoji.trim(),
      name: newName.trim(),
      order: items.length,
    });
    setNewEmoji('');
    setNewName('');
    refreshReactionEmojis();
    load();
  };

  const toggle = async (item: ReactionEmoji) => {
    await apiClient.put(`/api/admin/reactions/${item.id}`, { enabled: !item.enabled });
    refreshReactionEmojis();
    load();
  };

  const remove = async (id: string) => {
    if (!confirm('Удалить реакцию?')) return;
    await apiClient.delete(`/api/admin/reactions/${id}`);
    refreshReactionEmojis();
    load();
  };

  const move = async (item: ReactionEmoji, direction: 'up' | 'down') => {
    const newOrder = direction === 'up' ? item.order - 1 : item.order + 1;
    await apiClient.put(`/api/admin/reactions/${item.id}`, { order: newOrder });
    refreshReactionEmojis();
    load();
  };

  const addDefault = async (emoji: string, name: string) => {
    await apiClient.post('/api/admin/reactions', {
      emoji, name, order: items.length, enabled: true,
    });
    refreshReactionEmojis();
    load();
  };

  const addAllDefaults = async () => {
    const existing = new Set(items.map((i) => i.emoji));
    const missing = DEFAULT_EMOJIS.filter((d) => !existing.has(d.emoji));
    for (let i = 0; i < missing.length; i++) {
      await apiClient.post('/api/admin/reactions', {
        emoji: missing[i].emoji, name: missing[i].name,
        order: items.length + i, enabled: true,
      });
    }
    refreshReactionEmojis();
    load();
  };

  const existingEmojis = new Set(items.map((i) => i.emoji));
  const missingDefaults = DEFAULT_EMOJIS.filter((d) => !existingEmojis.has(d.emoji));

  return (
    <div className="p-4 max-w-3xl">
      <h2 className="text-xl font-semibold text-surface-100 mb-4">Управление реакциями</h2>

      <div className="bg-surface-900 border border-surface-800 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-surface-200 mb-3">Добавить реакцию</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={newEmoji}
            onChange={(e) => setNewEmoji(e.target.value)}
            placeholder="👍"
            maxLength={8}
            className="w-16 text-center text-2xl bg-surface-800 border border-surface-700 rounded px-2 py-1 text-surface-100"
          />
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Название (например: нравится)"
            className="flex-1 bg-surface-800 border border-surface-700 rounded px-3 py-1 text-surface-100 text-sm"
          />
          <button
            onClick={add}
            disabled={!newEmoji.trim() || !newName.trim()}
            className="px-3 py-1 rounded bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Добавить
          </button>
        </div>
        <p className="text-xs text-surface-500 mt-2">
          Дефолтный набор (👍❤️😂🎉😮😢🔥👏) <strong>всегда доступен</strong> клиентам.
          Добавь дефолт ниже если хочешь его отключить.
        </p>
      </div>

      {/* Default emojis not yet in DB — shown so admin can disable them */}
      {missingDefaults.length > 0 && (
        <div className="bg-surface-900 border border-surface-800 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-surface-200">Дефолтные (можно отключить)</h3>
            <button
              onClick={addAllDefaults}
              className="px-2 py-1 text-xs rounded bg-brand-600 hover:bg-brand-500 text-white"
            >
              Добавить все
            </button>
          </div>
          <p className="text-xs text-surface-500 mb-3">
            Эти эмодзи клиенты видят по умолчанию. Добавь в список чтобы управлять
            (отключить, удалить, переупорядочить).
          </p>
          <div className="flex flex-wrap gap-2">
            {missingDefaults.map((d) => (
              <button
                key={d.emoji}
                onClick={() => addDefault(d.emoji, d.name)}
                className="flex items-center gap-1.5 px-2 py-1 rounded border border-dashed border-surface-600 hover:border-brand-500 hover:bg-surface-800 text-sm text-surface-300"
              >
                <span className="text-lg">{d.emoji}</span>
                <span className="text-xs">{d.name}</span>
                <Plus size={12} className="text-brand-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-surface-500 text-sm">Загрузка...</p>
      ) : (
        <div className="space-y-2">
          {items.length === 0 && (
            <p className="text-surface-500 text-sm">Реакций нет. Используются дефолтные.</p>
          )}
          {items.map((item, idx) => (
            <div
              key={item.id}
              className="flex items-center gap-3 bg-surface-900 border border-surface-800 rounded-lg p-3"
            >
              <span className="text-2xl w-10 text-center">{item.emoji}</span>
              <div className="flex-1">
                <p className="text-surface-100 text-sm">{item.name}</p>
                <p className="text-surface-500 text-xs">порядок: {item.order}</p>
              </div>
              <button
                onClick={() => move(item, 'up')}
                disabled={idx === 0}
                className="p-1 text-surface-400 hover:text-surface-200 disabled:opacity-30"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => move(item, 'down')}
                disabled={idx === items.length - 1}
                className="p-1 text-surface-400 hover:text-surface-200 disabled:opacity-30"
              >
                <ArrowDown size={14} />
              </button>
              <button
                onClick={() => toggle(item)}
                className={`px-2 py-1 rounded text-xs ${
                  item.enabled
                    ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-600/50'
                    : 'bg-surface-800 text-surface-500'
                }`}
              >
                {item.enabled ? 'Включена' : 'Выключена'}
              </button>
              <button
                onClick={() => remove(item.id)}
                className="p-1 text-red-400 hover:text-red-300"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
